import moment from "moment";
import { BelongingIdToKorean, ScootStatusToKorean } from "../../../utils/Translate";
import { LanguageType } from "../../common/language/language.interface";
import {
  VehicleListV2Query,
  VehicleListV2Selection,
  VehicleListV2FilterValue,
  VehicleListV2Item,
  VehicleListV2QuickFilter,
  VehicleListV2Sort,
} from "./scooter.interface";

export function vehicleListV2SortDirection(sort: VehicleListV2Sort, order: "ascend" | "descend"): "ASC" | "DESC" {
  const descending = sort === "BATTERY" || sort === "ID" ? order === "descend" : order === "ascend";
  return descending ? "DESC" : "ASC";
}

const COLLECT_MODELS = ["S7", "S8", "S9", "S11", "W1", "W7", "W9"];
const COLLECT_STATUSES = ["WaitingForCapture", "Missing", "MissingThree", "WaitingForReallocate", "WaitingForChangeBattery"] as const;
const DISUSE_STATUSES = ["Cargo", "Ready", "Reallocated", "Captured", "CapturedForReallocating", "Missing", "MissingTwo", "MissingThree", "Lost", "Exception", "Report", "WaitingForReallocate", "CargoForReady", "WaitingForChangeBattery"] as const;

export function toggleVehicleListV2QuickFilter(
  value: VehicleListV2FilterValue,
  active: { collect: boolean; disuse: boolean },
  toggled: VehicleListV2QuickFilter,
  isMainBand: boolean,
  currentBandId?: string,
): { value: VehicleListV2FilterValue; active: { collect: boolean; disuse: boolean } } {
  const next = {
    collect: toggled === "COLLECT" ? !active.collect : active.collect,
    disuse: toggled === "DISUSE" ? !active.disuse : active.disuse,
  };
  if (next.collect) return {
    active: next,
    value: {
      ...value, quickFilter: "COLLECT", bandIds: currentBandId ? [currentBandId] : undefined,
      allBands: false, models: COLLECT_MODELS, statuses: [...COLLECT_STATUSES],
    },
  };
  if (next.disuse) return {
    active: next,
    value: { ...value, quickFilter: "DISUSE", statuses: [...DISUSE_STATUSES] },
  };
  return {
    active: next,
    value: {
      ...value, quickFilter: undefined,
      bandIds: isMainBand && currentBandId ? [currentBandId] : undefined,
      allBands: false, models: undefined,
      statuses: isMainBand ? ["Ready"] : undefined,
    },
  };
}

export function vehicleListV2ExcelRows(items: VehicleListV2Item[], languageType: LanguageType, bandNames: Map<string, string>, lastRideAtByVehicleId: Record<string, string | null> = {}) {
  return items.map((item) => {
    const allocatedDays = item.allocatedDays !== undefined ? item.allocatedDays : item.allocatedAt ? moment().utc().diff(moment(item.allocatedAt), "days") : "";
    const date = (value?: string) => value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "";
    const common = {
      qr: item.shortImei,
      model: item.smodel,
      band: bandNames.get(item.bandId),
      owner: BelongingIdToKorean[item.belongingId],
      battery: `${item.battery}%`,
      status: ScootStatusToKorean[item.status],
      riding: item.riding ? "O" : "X",
      locked: item.locked ? "O" : "X",
      iotBroken: item.online ? "X" : "O",
      allocatedDays: allocatedDays ? `${allocatedDays}${languageType === "KR" ? "일" : "days"}` : "",
      inspected: item.inspected ? "O" : "X",
      gpsUpdatedAt: date(item.gpsUpdatedAt),
      dataUpdatedAt: date(item.dataUpdatedAt),
      lastWorkAt: date(item.lastWorkAt),
      lastRideAt: lastRideAtByVehicleId[item.id] ? moment.utc(lastRideAtByVehicleId[item.id]).local().format("YYYY-MM-DD HH:mm:ss") : "",
    };
    if (languageType === "EN") return {
      QR: common.qr,
      device_model: common.model,
      region_name: common.band,
      device_owner: common.owner,
      battery: common.battery,
      device_status: common.status,
      ride_status: common.riding,
      lock_status: common.locked,
      iot_status: common.iotBroken,
      placement_date: common.allocatedDays,
      inspection_status: common.inspected,
      last_gps: common.gpsUpdatedAt,
      last_data: common.dataUpdatedAt,
      last_work: common.lastWorkAt,
      last_ride: common.lastRideAt,
    };
    return {
      QR: common.qr,
      "기기 모델": common.model,
      "소속 밴드": common.band,
      "기기 소유주": common.owner,
      "배터리 잔량": common.battery,
      "수거배치 상태": common.status,
      "라이드 여부": common.riding,
      "잠김 여부": common.locked,
      "iot 고장 여부": common.iotBroken,
      "배치일시": common.allocatedDays,
      "전수조사 여부": common.inspected,
      "마지막 GPS 수신": common.gpsUpdatedAt,
      "마지막 데이터 수신": common.dataUpdatedAt,
      "마지막 작업일자": common.lastWorkAt,
      "마지막 라이드": common.lastRideAt,
    };
  });
}

export function shouldResetVehicleListFilters(
  previousBandId?: string,
  nextBandId?: string
): boolean {
  return Boolean(previousBandId && nextBandId && previousBandId !== nextBandId);
}

export function compactVehicleListV2Query(
  filters: VehicleListV2FilterValue,
  identifiers: { qr: string; imei: string }
): VehicleListV2Query {
  return Object.entries({ ...filters, timezoneOffsetMinutes: new Date().getTimezoneOffset(), timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone, qr: identifiers.qr || undefined, imei: identifiers.imei || undefined }).reduce(
    (result, [key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return result;
      }
      return { ...result, [key]: value };
    },
    {} as VehicleListV2Query
  );
}

export function createVehicleListV2Selection(args: {
  querySelected: boolean;
  query: VehicleListV2Query;
  selectedIds: string[];
  excludedIds: string[];
  totalElements: number;
}): { selection: VehicleListV2Selection | null; selectedCount: number } {
  const { querySelected, query, selectedIds, excludedIds, totalElements } = args;
  const selectedCount = querySelected
    ? Math.max(0, totalElements - excludedIds.length)
    : selectedIds.length;

  if (selectedCount === 0) return { selection: null, selectedCount };
  if (querySelected) {
    return {
      selection: { mode: "QUERY", query, excludedVehicleIds: excludedIds },
      selectedCount,
    };
  }
  return {
    selection: { mode: "IDS", vehicleIds: selectedIds },
    selectedCount,
  };
}
